/**
 * CTAManager — gestión y visibilidad de CTAs del sitio (defaults + overrides geo).
 *
 * Dos secciones:
 *
 *  1. **Estado actual (read-only)** — muestra el valor "default" de cada
 *     CTA tal como viene de las env vars del deploy / Cloudflare Worker.
 *     Útil para verificar qué está configurado a nivel infra.
 *
 *  2. **Overrides geo-targeted (editable)** — tabla CRUD respaldada por
 *     `cta_overrides` (DB) + cache KV (Upstash/Postgres). Permite:
 *       • cambiar precios/URLs/toggles por país, provincia o ciudad
 *       • programar fechas de inicio/fin (ofertas temporales)
 *       • propagar cambios <1s a producción sin redeploy
 *     Resolución: city > province > country > global > env var fallback.
 *
 * Categorías cubiertas:
 *   - Telegram Basic (URL grupo + precio) — campo `broadcast_href` para BaseTemplate (WhatsApp broadcast)
 *   - VIP — campo `menu_wishlist_url` para BaseTemplate / internacional (menú / wishlist)
 *   - Free Channel (URL canal público)
 *   - Special Offer — campo `tally_embed_src` para BaseTemplate (embed Tally contacto)
 *   - Video (Mux playback IDs)
 *   - Worker (info-only, edición en Cloudflare dashboard)
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { ConfigOwnerCallout } from "@/components/admin/ConfigOwnerCallout";
import { CTAOverridesSection } from "@/components/admin/CTAOverridesSection";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { BRAND_SITE_HOST } from "@/lib/brandConfig";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  Clock,
  Crown,
  ExternalLink,
  Film,
  Globe,
  Loader2,
  MessageCircle,
  PartyPopper,
  RefreshCw,
  Send,
  Sparkles,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { CtaConfig, CtaStatusResponse } from "../../../api/admin/cta-status";

const CATEGORY_META: Record<
  CtaConfig["category"],
  {
    label: string;
    icon: typeof Sparkles;
    color: string;
    description: string;
  }
> = {
  telegram_basic: {
    label: "Telegram Basic",
    icon: Send,
    color: "text-sky-500",
    description: "Plan Basic ($6.99/mes) con grupo privado de Telegram",
  },
  vip_membership: {
    label: "VIP Membership",
    icon: Crown,
    color: "text-amber-500",
    description: "Plan VIP con video Mux + checkout Stripe/MP",
  },
  free_channel: {
    label: "Free Channel",
    icon: Sparkles,
    color: "text-emerald-500",
    description: "Canal público gratuito (top-of-funnel)",
  },
  special_offer: {
    label: "Special Offer",
    icon: PartyPopper,
    color: "text-pink-500",
    description: "Banner descuento global + countdown timer",
  },
  video: {
    label: "Mux Videos",
    icon: Film,
    color: "text-violet-500",
    description: "Playback IDs de los videos VIP/Basic",
  },
  whatsapp_broadcast: {
    label: "WhatsApp Broadcast",
    icon: MessageCircle,
    color: "text-green-500",
    description: "Precio ARS y USD del canal de WhatsApp (/whatsapp-broadcast)",
  },
  worker: {
    label: "Cloudflare Worker",
    icon: Globe,
    color: "text-blue-500",
    description: "Edge layer que routea AR y bloquea paths internacionales",
  },
};

function fmtValue(value: CtaConfig["value"]): string {
  if (value == null) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") {
    // Si parece precio en cents, mostrar también en formato
    if (value >= 100 && value <= 100000 && Number.isInteger(value)) {
      return `${value} (${(value / 100).toFixed(2)})`;
    }
    return String(value);
  }
  return String(value);
}

export default function CTAManager() {
  const { getAuthHeaders } = useAdminAuth();
  const [data, setData] = useState<CtaStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/cta-status", { headers });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setError(errBody.error || `HTTP ${res.status}`);
        return;
      }
      const json = (await res.json()) as CtaStatusResponse;
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Group ctas por categoría
  const grouped: Record<CtaConfig["category"], CtaConfig[]> = {
    telegram_basic: [],
    vip_membership: [],
    free_channel: [],
    special_offer: [],
    video: [],
    worker: [],
  };
  if (data) {
    for (const cta of data.ctas) {
      grouped[cta.category]?.push(cta);
    }
  }

  const totalActive = data?.ctas.filter((c) => c.active).length ?? 0;
  const totalInactive = data?.ctas.filter((c) => !c.active).length ?? 0;

  return (
    <AdminPageShell width="md">
      <AdminPageHeader
        description={
          <span>
            Overrides de infra (KV) en <code>{BRAND_SITE_HOST}</code>. No es cobro DualCTA ni
            wishlist.
          </span>
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchData()}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            )}
            Refrescar
          </Button>
        }
      />
      <ConfigOwnerCallout
        variant="info"
        owns="cta_overrides (pipeline edge / Worker KV)."
        notHere="VIP Stripe DualCTA → Home por país. Wishlist/cumpleaños → CTA por región. Mux home → Home por país."
        ownerPath="/admin/country-templates"
        ownerLabel="Home por país (DualCTA)"
      />

      {/* Summary banner */}
      {data && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-emerald-500 tabular-nums">
                  {totalActive}
                </span>
                <span className="text-sm text-muted-foreground">activos</span>
              </div>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-muted-foreground tabular-nums">
                  {totalInactive}
                </span>
                <span className="text-sm text-muted-foreground">inactivos</span>
              </div>
              <div className="h-6 w-px bg-border" />
              <div className="text-xs text-muted-foreground">
                Brand: <strong>{data.brand_name}</strong> ({data.brand_source_key})
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="pt-6">
            <p className="text-sm text-red-400 flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              Error: {error}
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Overrides geo-targeted (editable, propagación KV <1s) ─────────── */}
      <CTAOverridesSection />

      {/* Loading skeletons */}
      {loading && !data && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      )}

      {/* Grouped sections */}
      {data &&
        (Object.entries(grouped) as Array<[CtaConfig["category"], CtaConfig[]]>)
          .filter(([, items]) => items.length > 0)
          .map(([category, items]) => {
            const meta = CATEGORY_META[category];
            return (
              <div key={category} className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className={cn("p-1.5 rounded-md bg-muted/50")}>
                    <meta.icon className={cn("w-4 h-4", meta.color)} />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider">
                      {meta.label}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {meta.description}
                    </p>
                  </div>
                  <div className="h-px flex-1 bg-border ml-2" />
                </div>

                <div className="space-y-2">
                  {items.map((cta) => (
                    <Card
                      key={cta.id}
                      className={cn(!cta.active && "opacity-70")}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="space-y-1 flex-1 min-w-0">
                            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                              {cta.active ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                              ) : (
                                <XCircle className="w-4 h-4 text-muted-foreground shrink-0" />
                              )}
                              <span>{cta.label}</span>
                              {cta.env_var && (
                                <code className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                  {cta.env_var}
                                </code>
                              )}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {cta.description}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                              Valor actual
                            </div>
                            <div className="font-mono text-xs break-all p-2 bg-muted/30 rounded mt-1">
                              {fmtValue(cta.value)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                              Editar en
                            </div>
                            <div className="text-xs mt-1 text-muted-foreground">
                              {cta.source}
                            </div>
                            {cta.fallback != null && (
                              <div className="text-[10px] mt-1 text-muted-foreground/60">
                                Fallback: <code>{String(cta.fallback)}</code>
                              </div>
                            )}
                          </div>
                        </div>
                        {cta.hint && (
                          <div className="text-xs text-amber-500/80 bg-amber-500/5 p-2 rounded border border-amber-500/20">
                            💡 {cta.hint}
                          </div>
                        )}
                        {/* Si valor es URL, mostrar botón "Abrir" */}
                        {typeof cta.value === "string" &&
                          /^https?:\/\//.test(cta.value) && (
                            <Button variant="ghost" size="sm" asChild>
                              <a
                                href={cta.value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-7 px-2 text-xs"
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                Abrir URL
                              </a>
                            </Button>
                          )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}

      {/* Footer info */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground space-y-2">
              <p>
                <strong>Cómo funciona:</strong> el frontend pide a{" "}
                <code>/api/cta-resolved</code> los CTAs aplicables al geo del
                visitante (headers CF/Worker). El resolver mergea
                priority DESC: <code>ciudad &gt; provincia &gt; país &gt; global
                &gt; env var fallback</code>.
              </p>
              <p>
                <strong>Defaults (read-only)</strong> vienen de env vars de
                Railway — para cambiarlos requiere redeploy (~3-5 min).{" "}
                <strong>Overrides (editable)</strong> viven en la tabla{" "}
                <code>cta_overrides</code> y se cachean en KV — propagación
                &lt;1s entre regiones, sin redeploy.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </AdminPageShell>
  );
}
